"use client"
import { CourseBadges } from "./course-badge"
import type { Course } from "@/pages/courses/data/courses"
import { Baby, Users, CheckCircle2 } from "lucide-react"

interface CourseCardProps {
  course: Course
  index?: number
  delay?: number
}

export function CourseCard({ course, index = 0, delay = 0 }: CourseCardProps) {
  return (
    <a
      href={`/courses/${course.id}`}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${course.title}`}
      className="cursor-pointer relative group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-[#A8FF51] overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#A8FF51]/50"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] bg-gray-100">
        <img
          src={course.image || "/placeholder.jpg"}
          alt={course.imageAlt || course.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {/* Badges */}
        {course.badges && course.badges.length > 0 && (
          <div className="absolute left-3 top-3">
            <CourseBadges badges={course.badges} />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
      </div>
      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#245E51] transition-colors line-clamp-2 min-h-[3.25rem]">
          {course.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 min-h-[3.6rem]">
          {course.description}
        </p>

        {/* Simplified Info: Level + Suitable For badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-gray-50 border border-gray-100 text-gray-700">
            {course.level}
          </span>
          {course.suitableFor && course.suitableFor.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 border border-blue-100 text-blue-700">
              {course.suitableFor.includes("kids") ? (
                <>
                  <Baby className="w-3 h-3" />
                  <span>For Kids</span>
                </>
              ) : course.suitableFor.includes("women") ? (
                <>
                  <Users className="w-3 h-3" />
                  <span>For Women</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3 h-3" />
                  <span>For All</span>
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </a>
  )
}
